import { createContext, useContext, useState, useEffect, useRef } from "react";
import * as data from "@/data/mockData";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authApi = async (path, body, method = "POST") => {
  const res = await fetch(`${API}/api/auth${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.detail || "Something went wrong");
  return json;
};
const dataGet = async () => {
  const res = await fetch(`${API}/api/workspace/data`, { credentials: "include" });
  if (!res.ok) throw new Error("Could not load workspace data");
  return res.json();
};
const dataPut = async (snap) => {
  try {
    await fetch(`${API}/api/workspace/data`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify(snap),
    });
  } catch { /* transient — will retry on next change */ }
};
const adminPost = async (path) => {
  const res = await fetch(`${API}/api/admin${path}`, { method: "POST", credentials: "include" });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.detail || "Action failed");
  return json;
};

const FgpContext = createContext(null);
export const useFgp = () => useContext(FgpContext);

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* */ } };
const safeRemove = (k) => { try { localStorage.removeItem(k); } catch { /* */ } };

const SAVED_KEY = "fgp_data";
const loadSaved = () => { try { const r = safeGet(SAVED_KEY); return r ? JSON.parse(r) : null; } catch { return null; } };

// One-time fix: earlier demo seeds used two mismatched shared photos. Remap any stored
// avatar that still points at those old images back to the correct per-person photo.
const OLD_AVATARS = ["1614786269829", "1718209881007"];
const isOldAvatar = (u) => typeof u === "string" && OLD_AVATARS.some((o) => u.includes(o));
const migrateSaved = (s) => {
  if (!s) return s;
  const byId = Object.fromEntries(data.people.map((p) => [p.id, p.avatar]));
  const people = (s.people || []).map((p) => (isOldAvatar(p.avatar) && byId[p.id]) ? { ...p, avatar: byId[p.id] } : p);
  let currentUser = s.currentUser;
  if (currentUser && isOldAvatar(currentUser.avatar)) {
    currentUser = { ...currentUser, avatar: currentUser.role === "Principal" ? data.principal.avatar : data.currentUser.avatar };
  }
  return { ...s, people, currentUser };
};

const uid = (p) => `${p}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

// Advance a due date by the recurrence interval (for auto-generating the next occurrence).
const advanceDate = (dateStr, recurring) => {
  const base = (dateStr && dateStr !== "—") ? new Date(dateStr) : new Date();
  const d = isNaN(base.getTime()) ? new Date() : base;
  if (recurring === "daily") d.setDate(d.getDate() + 1);
  else if (recurring === "weekly") d.setDate(d.getDate() + 7);
  else if (recurring === "monthly") d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

export const FgpProvider = ({ children }) => {
  const [authed, setAuthed] = useState(false);
  const [booting, setBooting] = useState(true);

  const [currentUser, setCurrentUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [people, setPeople] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [trips, setTrips] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [adminMode, setAdminMode] = useState(false);
  const [viewAs, setViewAs] = useState(false);

  const hydratedRef = useRef(false);
  const syncTimer = useRef(null);

  // Persist the whole workspace snapshot to the backend (debounced), isolated per tenant.
  useEffect(() => {
    if (!hydratedRef.current || !authed || !workspace?.id || viewAs) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    const snap = { people, contacts, documents, renewals, tasks, events, alerts, notes, notifications, workspaceUsers, taskCategories, trips, expenses, budgets };
    syncTimer.current = setTimeout(() => { dataPut(snap); }, 700);
    return () => { if (syncTimer.current) clearTimeout(syncTimer.current); };
  }, [authed, workspace, viewAs, people, contacts, documents, renewals, tasks, events, alerts, notes, notifications, workspaceUsers, taskCategories, trips, expenses, budgets]);

  const applySnapshot = (d = {}) => {
    setPeople(d.people || []); setContacts(d.contacts || []); setDocuments(d.documents || []);
    setRenewals(d.renewals || []); setTasks(d.tasks || []); setEvents(d.events || []);
    setAlerts(d.alerts || []); setNotes(d.notes || []); setNotifications(d.notifications || []);
    setWorkspaceUsers(d.workspaceUsers || []); setTaskCategories(d.taskCategories || []);
    setTrips(d.trips || []); setExpenses(d.expenses || []); setBudgets(d.budgets || []);
  };
  const seedDemoData = () => applySnapshot({
    people: data.people, contacts: data.contacts, documents: data.documents,
    renewals: data.renewals, tasks: data.tasks, events: data.events, alerts: data.alerts,
    notes: data.voiceNotes, notifications: data.notifications, workspaceUsers: data.workspaceUsers,
    taskCategories: data.taskCategories, trips: data.trips, expenses: data.expenses, budgets: data.budgets,
  });

  // Load this workspace's data from the backend. Seed the demo workspace on first run;
  // start new tenants clean (just the default task categories).
  const hydrate = async (ws, { fresh = false } = {}) => {
    hydratedRef.current = false;
    try {
      const d = await dataGet();
      if (!fresh && !d.empty) applySnapshot(d);
      else if (!fresh && ws?.isDemo) seedDemoData();
      else applySnapshot({ taskCategories: data.taskCategories });
    } catch {
      applySnapshot({ taskCategories: data.taskCategories });
    }
    hydratedRef.current = true;
  };

  // Maps a backend auth response onto the app, then loads the tenant's data.
  const applyAuthResult = async (r, { fresh = false } = {}) => {
    const u = r.user || {};
    const isAdmin = !!u.isPlatformAdmin;
    const isDemo = !!r.workspace?.isDemo;
    setCurrentUser({
      id: u.id, name: u.name, email: u.email,
      role: isAdmin ? "Platform Administrator" : (u.role === "workspace_owner" ? "Workspace Owner" : (u.role || "Member")),
      access: u.access || "Owner", isPlatformAdmin: isAdmin,
      authProvider: u.authProvider || "password",
      // Only the demo workspace gets the sample avatar; real/fresh tenants start blank (initials fallback).
      avatar: u.avatar || (isDemo ? data.currentUser.avatar : ""),
    });
    const ws = r.workspace
      ? { ...data.workspace, name: r.workspace.name, plan: r.workspace.plan, members: r.workspace.members ?? 1, id: r.workspace.id, isDemo: r.workspace.isDemo }
      : (isAdmin ? { ...data.workspace, name: "Platform HQ", plan: "—", members: 0, id: null, isDemo: false } : null);
    setWorkspace(ws);
    setAdminMode(isAdmin);
    setViewAs(!!r.viewAs);
    setAuthed(true);
    await hydrate(ws, { fresh });
    if (fresh) {
      setWorkspaceUsers([{ id: u.id, name: u.name, email: u.email, role: "Workspace Owner", access: "Owner", status: "active", lastActive: "Active now", avatar: u.avatar || "" }]);
    }
    setBooting(false);
  };

  const login = async (email, password) => { const r = await authApi("/login", { email, password }); await applyAuthResult(r); return r; };
  const demoLogin = async () => { const r = await authApi("/demo"); await applyAuthResult(r); return r; };
  const googleSession = async (session_id) => { const r = await authApi("/google/session", { session_id }); await applyAuthResult(r); return r; };
  const loginAsOwner = async (email, password) => {
    const r = await authApi("/login", { email, password });
    if (!r.user?.isPlatformAdmin) throw new Error("This account is not a platform owner");
    await applyAuthResult(r);
    return r;
  };
  const register = async (form = {}) => {
    const r = await authApi("/register", { name: form.name, email: form.email, password: form.password, workspaceName: form.workspaceName || undefined });
    await applyAuthResult(r, { fresh: true });
    return r;
  };
  const changePassword = async (current_password, new_password) => {
    await authApi("/change-password", { current_password, new_password });
    return true;
  };
  const logout = async () => {
    try { await authApi("/logout"); } catch { /* */ }
    hydratedRef.current = false;
    setAuthed(false); setAdminMode(false); setViewAs(false); setCurrentUser(null); setWorkspace(null);
    applySnapshot({});
  };

  // ----- Platform owner: view-as (read-only impersonation) -----
  const beginViewAs = async (wid) => {
    const r = await adminPost(`/workspaces/${wid}/view-as`);
    const me = await authApi("/me", null, "GET");
    await applyAuthResult(me);
    return r;
  };
  const stopViewAs = async () => {
    try { await adminPost(`/view-as/stop`); } catch { /* */ }
    const me = await authApi("/me", null, "GET");
    await applyAuthResult(me);
  };

  // Restore session from the backend on load.
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles OAuth return
    (async () => {
      try {
        const r = await authApi("/me", null, "GET");
        await applyAuthResult(r);
      } catch {
        setAuthed(false); setAdminMode(false);
        setBooting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- People -----
  const addPerson = (p) => {
    const person = {
      id: uid("p"), avatar: "", email: "—", phone: "—",
      location: "—", dob: "1990-01-01", tags: [], ...p,
    };
    setPeople((prev) => [...prev, person]);
    return person;
  };
  const updatePerson = (id, patch) => setPeople((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p));
  const deletePerson = (id) => setPeople((prev) => prev.filter((p) => p.id !== id));

  // ----- Contacts (with person links) -----
  const addContact = (c) => {
    const contact = { id: uid("c"), notes: "", linkedPeople: [], phone: "—", email: "—", address: "—", ...c };
    setContacts((prev) => [...prev, contact]);
    return contact;
  };
  const toggleContactLink = (contactId, personId) => {
    setContacts((prev) => prev.map((c) => {
      if (c.id !== contactId) return c;
      const has = (c.linkedPeople || []).includes(personId);
      return { ...c, linkedPeople: has ? c.linkedPeople.filter((x) => x !== personId) : [...(c.linkedPeople || []), personId] };
    }));
  };
  const getContactsForPerson = (personId) => contacts.filter((c) => (c.linkedPeople || []).includes(personId));

  // ----- Documents -----
  const addDocument = (d) => {
    const doc = { id: uid("d"), status: "valid", uploaded: new Date().toISOString().slice(0, 10), person: "ws", expiry: "—", number: "", notes: "", ...d };
    setDocuments((prev) => [doc, ...prev]);
    return doc;
  };
  const updateDocument = (id, patch) => setDocuments((prev) => prev.map((d) => d.id === id ? { ...d, ...patch } : d));
  const deleteDocument = (id) => setDocuments((prev) => prev.filter((d) => d.id !== id));

  // ----- Renewals -----
  const addRenewal = (r) => {
    const ren = { id: uid("r"), status: "scheduled", owner: "Household", amount: "—", ...r };
    setRenewals((prev) => [...prev, ren]);
    return ren;
  };
  const updateRenewal = (id, patch) => setRenewals((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
  // Mark a renewal as renewed: roll the due date forward by a year, set it back on-track,
  // roll its linked document forward too, and clear any matching "Renewal" alert.
  const resolveRenewal = (id) => {
    let title = "", linkedDoc = null;
    setRenewals((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      title = r.title; linkedDoc = r.linkedDoc;
      const base = r.due && r.due !== "—" ? new Date(r.due) : new Date();
      const d = isNaN(base.getTime()) ? new Date() : base;
      d.setFullYear(d.getFullYear() + 1);
      return { ...r, status: "scheduled", due: d.toISOString().slice(0, 10) };
    }));
    if (linkedDoc) {
      setDocuments((prev) => prev.map((doc) => {
        if (doc.id !== linkedDoc) return doc;
        const base = doc.expiry && doc.expiry !== "—" ? new Date(doc.expiry) : new Date();
        const d = isNaN(base.getTime()) ? new Date() : base;
        d.setFullYear(d.getFullYear() + 1);
        return { ...doc, status: "valid", expiry: d.toISOString().slice(0, 10) };
      }));
    }
    const tokens = title.toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 3);
    setAlerts((prev) => prev.filter((a) => {
      if (a.category !== "Renewal") return true;
      const hay = `${a.title} ${a.detail || ""}`.toLowerCase();
      return !tokens.some((t) => hay.includes(t));
    }));
  };

  // ----- Events -----
  const addEvent = (e) => {
    const ev = { id: uid("e"), category: "Household", location: "—", person: "Household", time: "09:00", description: "", attendees: "", link: "", ...e };
    setEvents((prev) => [...prev, ev]);
    return ev;
  };
  const updateEvent = (id, patch) => setEvents((prev) => prev.map((e) => e.id === id ? { ...e, ...patch } : e));

  // ----- Tasks -----
  const toggleTask = (id) =>
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, status: t.status === "done" ? "todo" : "done" } : t));
  const setTaskStatus = (id, status) =>
    setTasks((prev) => {
      const target = prev.find((t) => t.id === id);
      const updated = prev.map((t) => t.id === id ? { ...t, status } : t);
      // Auto-generate the next occurrence when a recurring task is completed.
      if (target && status === "done" && target.status !== "done" && target.recurring && target.recurring !== "none") {
        const nextDue = advanceDate(target.due, target.recurring);
        const next = {
          ...target, id: uid("t"), status: "todo", due: nextDue,
          subtasks: (target.subtasks || []).map((s) => ({ ...s, done: false })),
        };
        setTimeout(() => toast.success(`Recurring task rescheduled — next due ${nextDue}`), 0);
        return [next, ...updated];
      }
      return updated;
    });
  const addTask = (t) => {
    const task = {
      id: uid("t"), status: "todo", priority: "medium", category: "cat_general", assignee: currentUser.name,
      person: "Household", due: "—", source: "manual",
      description: "", tags: [], subtasks: [], reminder: "", recurring: "none",
      ...(typeof t === "string" ? { title: t, source: "voice" } : t),
    };
    setTasks((prev) => [task, ...prev]);
    return task;
  };
  const updateTask = (id, patch) => setTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...patch } : t));

  // ----- Alerts -----
  const resolveAlert = (id) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  // ----- Notes (voice + text) -----
  const addNote = (note) => setNotes((prev) => [{ id: uid("v"), date: new Date().toISOString().slice(0, 10), ...note }, ...prev]);
  const updateNote = (id, patch) => setNotes((prev) => prev.map((n) => n.id === id ? { ...n, ...patch } : n));
  const deleteNote = (id) => setNotes((prev) => prev.filter((n) => n.id !== id));

  // ----- Notifications -----
  const markNotificationRead = (id) => setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  const markAllNotificationsRead = () => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  const unreadCount = notifications.filter((n) => !n.read).length;

  // ----- Workspace users -----
  const addWorkspaceUser = (u) => {
    const user = {
      id: uid("u"), name: "", email: "", role: "Personal Assistant", access: "Editor",
      status: "invited", lastActive: "Never", avatar: "", ...u,
    };
    setWorkspaceUsers((prev) => [...prev, user]);
    setWorkspace((w) => ({ ...w, members: (w.members || 0) + 1 }));
    return user;
  };
  const updateWorkspaceUser = (id, patch) => setWorkspaceUsers((prev) => prev.map((u) => u.id === id ? { ...u, ...patch } : u));
  const removeWorkspaceUser = (id) => {
    setWorkspaceUsers((prev) => prev.filter((u) => u.id !== id));
    setWorkspace((w) => ({ ...w, members: Math.max(1, (w.members || 1) - 1) }));
  };

  // ----- Profile / workspace / role -----
  const updateProfile = (patch) => setCurrentUser((prev) => ({ ...prev, ...patch }));
  const updateWorkspace = (patch) => setWorkspace((prev) => ({ ...prev, ...patch }));
  const toggleAdminMode = () => setAdminMode((m) => !m);

  // ----- Task categories (customisable) -----
  const addCategory = ({ name, color }) => {
    const cat = { id: uid("cat"), name: name?.trim() || "Untitled", color: color || "slate" };
    setTaskCategories((prev) => [...prev, cat]);
    return cat;
  };
  const updateCategory = (id, patch) => setTaskCategories((prev) => prev.map((c) => c.id === id ? { ...c, ...patch } : c));
  const deleteCategory = (id) => {
    setTaskCategories((prev) => prev.filter((c) => c.id !== id));
    setTasks((prev) => prev.map((t) => t.category === id ? { ...t, category: "cat_general" } : t));
  };

  // ----- Travel / trips -----
  const addTrip = (t) => {
    const trip = { id: uid("trip"), destination: "", purpose: "", startDate: "", endDate: "", travelers: "", status: "planning", flights: [], hotel: "", transfer: "", notes: "", ...t };
    setTrips((prev) => [trip, ...prev]);
    return trip;
  };
  const updateTrip = (id, patch) => setTrips((prev) => prev.map((t) => t.id === id ? { ...t, ...patch } : t));
  const deleteTrip = (id) => setTrips((prev) => prev.filter((t) => t.id !== id));

  // ----- Expenses -----
  const addExpense = (e) => {
    const exp = { id: uid("exp"), date: new Date().toISOString().slice(0, 10), merchant: "", category: "Other", amount: 0, currency: "£", status: "pending", person: "Household", notes: "", receipt: "", ...e };
    setExpenses((prev) => [exp, ...prev]);
    return exp;
  };
  const updateExpense = (id, patch) => setExpenses((prev) => prev.map((e) => e.id === id ? { ...e, ...patch } : e));
  const deleteExpense = (id) => setExpenses((prev) => prev.filter((e) => e.id !== id));

  // ----- Budgets -----
  const addBudget = (b) => {
    const budget = { id: uid("b"), name: "", occasion: "", currency: "£", createdAt: new Date().toISOString().slice(0, 10), items: [], ...b };
    setBudgets((prev) => [budget, ...prev]);
    return budget;
  };
  const updateBudget = (id, patch) => setBudgets((prev) => prev.map((b) => b.id === id ? { ...b, ...patch } : b));
  const deleteBudget = (id) => setBudgets((prev) => prev.filter((b) => b.id !== id));

  const getPerson = (id) => people.find((p) => p.id === id);

  const value = {
    workspace, updateWorkspace, AVATARS: data.AVATARS, principal: data.principal,
    relationships: data.relationships, aiDailyBrief: data.aiDailyBrief,
    reportData: data.reportData, adminWorkspaces: data.adminWorkspaces,
    adminStats: data.adminStats, statusMeta: data.statusMeta,
    authed, booting, login, demoLogin, googleSession, logout, register, loginAsOwner, changePassword,
    currentUser, updateProfile, adminMode, toggleAdminMode,
    viewAs, beginViewAs, stopViewAs,
    people, addPerson, updatePerson, deletePerson, getPerson,
    contacts, addContact, toggleContactLink, getContactsForPerson,
    documents, addDocument, updateDocument, deleteDocument,
    renewals, addRenewal, updateRenewal, resolveRenewal,
    events, addEvent, updateEvent,
    tasks, toggleTask, setTaskStatus, addTask, updateTask,
    taskCategories, addCategory, updateCategory, deleteCategory,
    trips, addTrip, updateTrip, deleteTrip,
    expenses, addExpense, updateExpense, deleteExpense,
    budgets, addBudget, updateBudget, deleteBudget,
    alerts, resolveAlert,
    notes, addNote, updateNote, deleteNote,
    notifications, markNotificationRead, markAllNotificationsRead, unreadCount,
    workspaceUsers, addWorkspaceUser, updateWorkspaceUser, removeWorkspaceUser,
  };

  return <FgpContext.Provider value={value}>{children}</FgpContext.Provider>;
};
